<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { gsap } from 'gsap';
import { Flip } from 'gsap/Flip';
import { resources } from '../data/resources.js';

const root = ref(null);
const activeType = ref('all');
const orderedIds = ref([]);
const switching = ref(false);
let context;
let activeFlip;
let unlockTimer;

const filters = [
  { id:'all',label:'الكل',icon:'bi-grid',types:['website','app','research','center','software'] },
  { id:'website',label:'مواقع',icon:'bi-globe2',types:['website'] },
  { id:'app',label:'تطبيقات',icon:'bi-phone',types:['app'] },
  { id:'research',label:'بحوث ومجلات',icon:'bi-book',types:['research'] },
  { id:'center',label:'مراكز تعليمية',icon:'bi-mortarboard',types:['center'] },
  { id:'software',label:'برامج وأدوات',icon:'bi-pc-display',types:['software'] }
];

const usableResources = resources.filter(resource => resource.title && resource.image && resource.path);
const resourceById = new Map(usableResources.map(resource => [resource.id,resource]));

function resourcesForFilter(filterId) {
  const filter = filters.find(item => item.id === filterId) || filters[0];
  if (filter.id === 'all') {
    return filter.types
      .map(type => usableResources.find(resource => resource.type === type))
      .filter(Boolean)
      .slice(0,5);
  }
  return usableResources.filter(resource => filter.types.includes(resource.type)).slice(0,5);
}

orderedIds.value = resourcesForFilter('all').map(resource => resource.id);

const visibleResources = computed(() => orderedIds.value.map(id => resourceById.get(id)).filter(Boolean));
const featuredResource = computed(() => visibleResources.value[0]);
const secondaryResources = computed(() => visibleResources.value.slice(1));
const rightResources = computed(() => secondaryResources.value.length <= 2 ? secondaryResources.value : secondaryResources.value.slice(0,2));
const leftResources = computed(() => secondaryResources.value.length <= 2 ? [] : secondaryResources.value.slice(2,4));
const galleryCountClass = computed(() => `featured-count-${visibleResources.value.length}`);
const stats = computed(() => [
  { value:usableResources.length,label:'موردًا في الدليل' },
  { value:usableResources.filter(resource => resource.type === 'app').length,label:'تطبيقًا هاتفيًا' },
  { value:usableResources.filter(resource => resource.type === 'center').length,label:'مركزًا تعليميًا' },
  { value:usableResources.filter(resource => resource.type === 'research').length,label:'موردًا بحثيًا' }
].filter(stat => stat.value > 0));

const resourceImage = resource => resource?.detailImage || resource?.images?.[1] || resource?.image;
const resourceSubtitle = resource => resource?.category?.label || resource?.typeLabel;
const resourceIcon = resource => filters.find(filter => filter.id !== 'all' && filter.types.includes(resource?.type))?.icon || 'bi-book';

async function promoteResource(id) {
  if (switching.value || id === featuredResource.value?.id) return;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = matchMedia('(min-width: 992px)').matches;
  const cards = root.value?.querySelectorAll('[data-featured-flip]');
  const state = desktop && !reduceMotion && cards?.length ? Flip.getState(cards,{ props:'borderRadius' }) : null;
  const index = orderedIds.value.indexOf(id);
  if (index < 1) return;

  switching.value = true;
  const nextOrder = [...orderedIds.value];
  [nextOrder[0],nextOrder[index]] = [nextOrder[index],nextOrder[0]];
  orderedIds.value = nextOrder;
  await nextTick();

  activeFlip?.kill();
  if (state) {
    activeFlip = Flip.from(state,{ duration:.74,ease:'power3.inOut',absolute:true,nested:false,scale:false,prune:true });
  }
  if (!reduceMotion) {
    const featured = root.value?.querySelector('.featured-resource-main');
    gsap.fromTo(featured?.querySelector('.featured-resource-main-image'),{ scale:1.025 },{ scale:1,duration:.52,ease:'power3.out',clearProps:'transform' });
    gsap.fromTo(featured?.querySelectorAll('.featured-resource-copy > *'),{ autoAlpha:0,y:12 },{ autoAlpha:1,y:0,duration:.46,stagger:.045,ease:'power3.out',clearProps:'transform,opacity,visibility' });
  }
  clearTimeout(unlockTimer);
  unlockTimer = window.setTimeout(() => { switching.value = false; },reduceMotion ? 0 : 780);
}

async function selectFilter(filterId) {
  if (switching.value || filterId === activeType.value) return;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  switching.value = true;
  activeType.value = filterId;
  orderedIds.value = resourcesForFilter(filterId).map(resource => resource.id);
  await nextTick();
  if (!reduceMotion) {
    gsap.fromTo(root.value?.querySelectorAll('[data-featured-flip]'),
      { autoAlpha:0,y:12,clipPath:'inset(0 0 8% 0 round 20px)' },
      { autoAlpha:1,y:0,clipPath:'inset(0 0 0% 0 round 20px)',duration:.58,stagger:.065,ease:'power3.out',clearProps:'transform,opacity,visibility,clipPath' });
  }
  clearTimeout(unlockTimer);
  unlockTimer = window.setTimeout(() => { switching.value = false; },reduceMotion ? 0 : 620);
}

function runEntrance() {
  if (!root.value || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  context = gsap.context(() => {
    const timeline = gsap.timeline({ scrollTrigger:{ trigger:root.value,start:'top 76%',once:true },defaults:{ ease:'power3.out' } });
    timeline
      .from('.featured-gallery-title>span',{ yPercent:105,autoAlpha:0,duration:.86,ease:'power4.out' })
      .from('.featured-gallery-header p,.featured-gallery-header>i',{ y:12,autoAlpha:0,duration:.42,stagger:.08 },'-=.42')
      .from('.featured-resource-filter',{ clipPath:'inset(0 100% 0 0 round 999px)',stagger:.055,duration:.46 },'-=.2')
      .from('.featured-resource-main',{ clipPath:'inset(0 0 100% 0 round 24px)',scale:1.025,duration:.92 },'-=.12')
      .from('.featured-gallery-side--right .featured-resource-side-card',{ x:22,autoAlpha:0,stagger:.07,duration:.5 },'-=.58')
      .from('.featured-gallery-side--left .featured-resource-side-card',{ x:-22,autoAlpha:0,stagger:.07,duration:.5 },'-=.5')
      .from('.featured-resource-stats',{ clipPath:'inset(0 50% 0 50% round 18px)',duration:.55 },'-=.18')
      .from('.featured-resource-footer',{ y:10,autoAlpha:0,duration:.38 },'-=.22');
  },root.value);
}

onMounted(runEntrance);
onBeforeUnmount(() => {
  clearTimeout(unlockTimer);
  activeFlip?.kill();
  context?.revert();
});
</script>

<template>
  <section id="featured" ref="root" class="section featured featured-gallery-section" aria-labelledby="featured-gallery-heading">
    <div class="featured-gallery-wrap">
      <header class="featured-gallery-header">
        <h2 id="featured-gallery-heading" class="featured-gallery-title"><span>موارد مختارة من الدليل</span></h2>
        <p>اكتشف مجموعة مختارة من المصادر والموارد التي تخدم السنة وعلومها.</p>
        <i aria-hidden="true"></i>
      </header>

      <div class="featured-resource-filters" aria-label="تصفية الموارد حسب النوع">
        <button v-for="filter in filters" :key="filter.id" type="button" class="featured-resource-filter" :class="{active:activeType===filter.id}" :aria-pressed="activeType===filter.id" @click="selectFilter(filter.id)"><i class="bi" :class="filter.icon"></i><span>{{ filter.label }}</span></button>
      </div>

      <div v-if="featuredResource" class="featured-resource-gallery" :class="galleryCountClass">
        <aside v-if="rightResources.length" class="featured-gallery-side featured-gallery-side--right" aria-label="موارد مختارة إضافية">
          <button v-for="resource in rightResources" :key="resource.id" type="button" class="featured-resource-side-card" :class="`type-${resource.type}`" :data-featured-flip="resource.id" :data-flip-id="`featured-${resource.id}`" :aria-label="`عرض ${resource.title} كمورد رئيسي`" :aria-pressed="false" @click="promoteResource(resource.id)">
            <span class="featured-side-image"><img :src="resourceImage(resource)" :alt="resource.title"><i class="bi" :class="resourceIcon(resource)"></i></span>
            <span class="featured-side-copy"><small>{{ resource.typeLabel }}</small><b>{{ resource.title }}</b><em>{{ resourceSubtitle(resource) }}</em><i class="bi bi-arrow-left"></i></span>
          </button>
        </aside>

        <article class="featured-resource-main" :class="`type-${featuredResource.type}`" :data-featured-flip="featuredResource.id" :data-flip-id="`featured-${featuredResource.id}`">
          <img class="featured-resource-main-image" :src="resourceImage(featuredResource)" :alt="featuredResource.title">
          <div class="featured-resource-shade"></div>
          <span class="featured-resource-badge">المورد المميز</span>
          <div class="featured-resource-copy">
            <small><i class="bi" :class="resourceIcon(featuredResource)"></i>{{ featuredResource.typeLabel }}</small>
            <h3>{{ featuredResource.title }}</h3>
            <em>{{ resourceSubtitle(featuredResource) }}</em>
            <p>{{ featuredResource.shortDescription || featuredResource.description }}</p>
            <RouterLink :to="featuredResource.path">استكشف المورد <i class="bi bi-arrow-left"></i></RouterLink>
          </div>
        </article>

        <aside v-if="leftResources.length" class="featured-gallery-side featured-gallery-side--left" aria-label="موارد مختارة إضافية">
          <button v-for="resource in leftResources" :key="resource.id" type="button" class="featured-resource-side-card" :class="`type-${resource.type}`" :data-featured-flip="resource.id" :data-flip-id="`featured-${resource.id}`" :aria-label="`عرض ${resource.title} كمورد رئيسي`" :aria-pressed="false" @click="promoteResource(resource.id)">
            <span class="featured-side-image"><img :src="resourceImage(resource)" :alt="resource.title"><i class="bi" :class="resourceIcon(resource)"></i></span>
            <span class="featured-side-copy"><small>{{ resource.typeLabel }}</small><b>{{ resource.title }}</b><em>{{ resourceSubtitle(resource) }}</em><i class="bi bi-arrow-left"></i></span>
          </button>
        </aside>
      </div>

      <dl v-if="stats.length" class="featured-resource-stats" aria-label="إحصاءات الدليل">
        <div v-for="stat in stats" :key="stat.label"><dt>{{ stat.value }}</dt><dd>{{ stat.label }}</dd></div>
      </dl>

      <footer class="featured-resource-footer"><p>لم تجد ما تبحث عنه؟</p><RouterLink :to="{name:'search',query:{page:'1'}}">تصفح جميع الموارد <i class="bi bi-arrow-left"></i></RouterLink></footer>
    </div>
  </section>
</template>
